import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';

const DailyReportPage = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const apiBaseURL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const fetchDailyReport = async () => {
      const token = localStorage.getItem('employeeToken');
      if (!token) {
        window.location.href = "/employee-login";
        return;
      }

      try {
        const response = await axios.get(`${apiBaseURL}/api/daily-report`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setReportData(response.data);
      } catch (err) {
        setError('Failed to fetch daily report.');
      } finally {
        setLoading(false);
      }
    };

    fetchDailyReport();
  }, [apiBaseURL]);

  const formatDateToIST = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (loading) return <div>Loading Daily Report...</div>;
  if (error) return <div>{error}</div>;

  let cumulative = {
    new_subscriptions: 0,
    amount_collected_inc_gst: 0,
    amount_collected_ex_gst: 0,
    total_payout_eligible_amount: 0,
    total_withdrawn_amount: 0,
    eod_balance: 0
  };

const sortedReportData = [...reportData].sort(
  (a, b) => new Date(a.date) - new Date(b.date)
);


const chartData = sortedReportData.map(row => {

    const amountCollectedIncGST = parseFloat(row.amount_collected_inc_gst);
    const amountCollectedExGST = parseFloat(row.amount_collected_before_gst);
    const totalPayoutEligibleAmount = parseFloat(row.total_payout_eligible_amount);
    const eodBalance = amountCollectedExGST - totalPayoutEligibleAmount;

    cumulative.new_subscriptions += parseFloat(row.new_subscriptions);
    cumulative.amount_collected_inc_gst += amountCollectedIncGST;
    cumulative.amount_collected_ex_gst += amountCollectedExGST;
    cumulative.total_payout_eligible_amount += totalPayoutEligibleAmount;
    cumulative.total_withdrawn_amount += parseFloat(row.total_withdrawn_amount);
    cumulative.eod_balance += eodBalance;

    return {
      date: formatDateToIST(row.date),
      new_subscriptions: cumulative.new_subscriptions,
      amount_collected_inc_gst: cumulative.amount_collected_inc_gst,
      amount_collected_ex_gst: cumulative.amount_collected_ex_gst,
      total_payout_eligible_amount: cumulative.total_payout_eligible_amount,
      total_withdrawn_amount: cumulative.total_withdrawn_amount,
      eod_balance: cumulative.eod_balance
    };
  });

  
  const sortedChartData = [...chartData];


  const totalAmountCollectedIncGST = chartData[chartData.length - 1]?.amount_collected_inc_gst || 0;
  const totalAmountCollectedExGST = chartData[chartData.length - 1]?.amount_collected_ex_gst || 0;
  const totalPayoutEligibleAmount = chartData[chartData.length - 1]?.total_payout_eligible_amount || 0;
  const totalWithdrawnAmount = chartData[chartData.length - 1]?.total_withdrawn_amount || 0;

  const totalGSTAmount = totalAmountCollectedIncGST - totalAmountCollectedExGST;
  const payoutYetToBeWithdrawn = totalPayoutEligibleAmount - totalWithdrawnAmount;
  const subscriptionReserves = totalAmountCollectedExGST - totalPayoutEligibleAmount;
  const totalPayoutPlusReserves = totalWithdrawnAmount + payoutYetToBeWithdrawn + subscriptionReserves;

  const combinedStackedData = [
    {
      name: 'Payout Summary',
      payoutWithdrawn: totalWithdrawnAmount,
      payoutYetToWithdraw: payoutYetToBeWithdrawn > 0 ? payoutYetToBeWithdrawn : 0,
      subscriptionReserves: subscriptionReserves > 0 ? subscriptionReserves : 0,
      exGSTAmount: 0,
      gstAmount: 0,
      totalPayoutPlusReserves
    },
    {
      name: 'Amount Collected Breakdown',
      payoutWithdrawn: 0,
      payoutYetToWithdraw: 0,
      subscriptionReserves: 0,
      exGSTAmount: totalAmountCollectedExGST,
      gstAmount: totalGSTAmount,
      totalPayoutPlusReserves: 0
    }
  ];

  const metrics = [
    { title: 'New Subscriptions', dataKey: 'new_subscriptions', color: '#4F46E5' },
    { title: 'Amount Collected (Inc GST)', dataKey: 'amount_collected_inc_gst', color: '#10B981' },
    { title: 'Amount Collected (Exc GST)', dataKey: 'amount_collected_ex_gst', color: '#3B82F6' },
    { title: 'Total Payout Eligible Amount (₹)', dataKey: 'total_payout_eligible_amount', color: '#A855F7' },
    { title: 'Payout Withdrawn', dataKey: 'total_withdrawn_amount', color: '#F59E0B' },
    { title: 'EoD Balance', dataKey: 'eod_balance', color: '#EF4444' }
  ];

  const CustomCombinedTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;

      const showTotalFees =
        label === 'Amount Collected Breakdown' &&
        data.exGSTAmount !== undefined &&
        data.gstAmount !== undefined;

      const totalFees = showTotalFees
        ? data.exGSTAmount + data.gstAmount
        : 0;

      return (
        <div className="bg-white border border-gray-300 rounded p-3 shadow text-sm">
          <div className="font-semibold mb-1">{label}</div>
          {payload.map((entry) => {
            if (entry.value === 0) return null;
            return (
              <div key={entry.name} className="flex justify-between">
                <span>{entry.name}</span>
                <span>
                  {entry.value.toLocaleString('en-IN', {
                    style: 'currency',
                    currency: 'INR'
                  })}
                </span>
              </div>
            );
          })}
          {showTotalFees && (
            <div className="flex justify-between mt-2 border-t pt-2">
              <span className="font-semibold">Total Fees</span>
              <span className="font-semibold">
                {totalFees.toLocaleString('en-IN', {
                  style: 'currency',
                  currency: 'INR'
                })}
              </span>
            </div>
          )}
          {data.totalPayoutPlusReserves > 0 && !showTotalFees && (
            <div className="flex justify-between mt-2 border-t pt-2">
              <span className="font-semibold">Total Payout + Reserves</span>
              <span className="font-semibold">
                {data.totalPayoutPlusReserves.toLocaleString('en-IN', {
                  style: 'currency',
                  currency: 'INR'
                })}
              </span>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="p-6 ml-72">
      <h2 className="text-2xl font-semibold mb-4">Daily Report (Cumulative)</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {metrics.map(({ title, dataKey, color }) => (
          <div key={dataKey} className="bg-white p-4 rounded shadow">
            <h3 className="font-semibold mb-2">{title}</h3>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={sortedChartData} margin={{ top: 20, right: 30, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip
                  formatter={(value, name) => {
                    const currencyKeys = [
                      'amount_collected_inc_gst',
                      'amount_collected_ex_gst',
                      'total_payout_eligible_amount',
                      'total_withdrawn_amount',
                      'eod_balance'
                    ];
                    return currencyKeys.includes(name)
                      ? value.toLocaleString('en-IN', {
                          style: 'currency',
                          currency: 'INR'
                        })
                      : value.toLocaleString();
                  }}
                />
                <Legend />
                <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={3} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ))}
      </div>

      <div className="bg-white p-4 rounded shadow mb-8">
        <h3 className="font-semibold mb-2">Cumulative Payout & Collection Summary</h3>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart
            layout="vertical"
            data={combinedStackedData}
            margin={{ top: 20, right: 30, left: 180, bottom: 20 }}
            barCategoryGap={20}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 14 }} width={220} />
            <Tooltip content={<CustomCombinedTooltip />} />
            <Legend />
            <Bar dataKey="payoutWithdrawn" stackId="a" fill="#F59E0B" name="Withdrawn" />
            <Bar dataKey="payoutYetToWithdraw" stackId="a" fill="#FACC15" name="Yet to Withdraw" />
            <Bar dataKey="subscriptionReserves" stackId="a" fill="#10B981" name="Subscription Reserves" />
            <Bar dataKey="exGSTAmount" stackId="b" fill="#3B82F6" name="Amount (Ex GST)" />
            <Bar dataKey="gstAmount" stackId="b" fill="#EF4444" name="GST Amount" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default DailyReportPage;
